import React, { useEffect, useRef, useState } from 'react';
import { ChevronDownIcon } from '@radix-ui/react-icons';

import { OptionDto } from '../../dto/common/option.dto';

import styles from './Select.module.scss';
export interface SelectProps {
  label?: string | null;
  value?: string;
  addDataRow?: {
    label: string;
    onClick: () => void;
  };
  placeHolder?: string | null;
  options: OptionDto[];
  onSelect?: (el: any) => void;
  hasError?: boolean;
  disabled?: boolean;
  small?: boolean;
  medium?: boolean;
  takeDefaultFirst?: boolean;
  errorMessage?: string;
  name?: string;
  maxHeight?: number;
  icon?: React.ReactNode;
  className?: string;
}

// TODO: Re-implement later using Radix UI
export const Select = React.forwardRef<HTMLInputElement, SelectProps>(
  (
    {
      label,
      value,
      onSelect,
      options,
      placeHolder,
      name,
      disabled = false,
      takeDefaultFirst = false,
      hasError = false,
      small = false,
      medium = false,
      errorMessage,
      addDataRow,
      maxHeight = 30,
      className = '',
    },
    ref,
  ) => {
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [isOpenOnTop, setIsOpenOnTop] = useState<boolean>(false);
    const wrapperRef = useRef(null);
    const tookDefaultRef = useRef(false);
    const optionHeight = 40;

    useEffect(() => {
      // Run once. Guarding prevents an infinite loop when the parent passes an
      // inline `onSelect`/`options` (new reference each render), which would
      // otherwise re-fire this effect → onSelect → re-render → re-fire.
      if (takeDefaultFirst && !tookDefaultRef.current && options[0]) {
        tookDefaultRef.current = true;
        onSelect?.({
          target: {
            name: name,
            value: options[0].value,
          },
        });
      }
    }, [name, onSelect, options, takeDefaultFirst]);

    const handleOpen = (e: any) => {
      e.stopPropagation();

      if (!disabled) {
        const windowHeight = window.innerHeight;
        const dropdownHeight = Math.min(maxHeight, options.length * optionHeight);
        if (wrapperRef.current) {
          // @ts-ignore
          setIsOpenOnTop(
            // @ts-ignore
            wrapperRef.current.getBoundingClientRect().bottom + dropdownHeight >= windowHeight,
          );
        }
        setIsOpen(!isOpen);
      }
    };

    const handleOptionClick = (el: any) => {
      const e = {
        target: {
          name: name,
          value: el.value,
        },
      };

      if (onSelect) {
        onSelect(e);
      }

      setIsOpen(false);
    };

    const useOutsideAlerter = (ref: any) => {
      useEffect(() => {
        function handleClickOutside(event: any) {
          if (ref.current && !ref.current.contains(event.target)) {
            setIsOpen(false);
          }
        }

        // Bind the event listener
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
          // Unbind the event listener on clean up
          document.removeEventListener('mousedown', handleClickOutside);
        };
      }, [ref]);
    };

    useOutsideAlerter(wrapperRef);

    return (
      <div
        ref={wrapperRef}
        className={
          className
            ? `${styles['select-input-container']} ${className}`
            : styles['select-input-container']
        }
        data-small={small}
        data-medium={medium}
        data-disabled={disabled}
      >
        {label && (
          <div className={styles['label']} style={{ zIndex: -1 }}>
            {label}
          </div>
        )}

        <div
          className={styles['input']}
          ref={ref}
          onClick={handleOpen}
          data-small={small}
          data-medium={medium}
          data-disabled={disabled}
          data-has-error={hasError || !!errorMessage}
        >
          <div
            className={styles['text']}
            id="select-text"
            data-has-error={hasError || !!errorMessage}
            data-disabled={disabled}
          >
            {value
              ? options.find((el) => el.value === value)?.label
              : placeHolder
              ? placeHolder
              : options.find((el) => el.value === undefined) === undefined
              ? 'Select value'
              : options.find((el) => el.value === undefined)!.label}
          </div>

          <ChevronDownIcon color={'var(--select-text-color)'} />

          {isOpen && (
            <div
              className={styles['option']}
              id="option-menu"
              data-small={small}
              data-medium={medium}
              style={{ '--option-max-height': `${maxHeight}rem` } as React.CSSProperties}
            >
              <ul>
                {options!.map((el, key) => (
                  <li onClick={() => handleOptionClick(el)} key={key}>
                    {el.label}
                  </li>
                ))}
                {}
                {addDataRow && <li onClick={addDataRow?.onClick}>{addDataRow.label}</li>}
              </ul>
            </div>
          )}
        </div>

        {errorMessage && <span className={styles['error']}>{errorMessage}</span>}
      </div>
    );
  },
);

Select.displayName = 'Select';
